'use client';

import { c as _c } from "react/compiler-runtime";
import { hashDiagnosticEntity, refetchRealtimeQuery, useQueryClient } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef, useState } from 'react';
import { initialResolutionHold, isSettledPageReady, nextResolutionHold, type ResolutionHold, type ResolutionHoldMarket } from '@/lib/market/resolution-hold';
import { logger } from '@/lib/telemetry/logger';

/** Past the API's one-second hot cache, so the first refetch sees the transition. */
export const RESOLUTION_HOLD_FIRST_REFETCH_MS = 1_500;
export const RESOLUTION_HOLD_REFETCH_INTERVAL_MS = 2_000;
/** A settled page with partial data beats a page stuck on "Graduating". */
export const RESOLUTION_HOLD_TIMEOUT_MS = 20_000;
type QueryKey = Parameters<typeof refetchRealtimeQuery>[1];
interface UseResolutionHoldArgs {
  readonly marketPubkey: string;
  readonly market: ResolutionHoldMarket | undefined;
  readonly winnerRowLoaded: boolean;
  readonly winnersQueryKey: QueryKey;
}

/**
 * Tracks the page's {@link ResolutionHold}. While holding it refetches the
 * market row and the winners row until the settled page's data is in (realtime
 * events never carry the pool, prize total or token price), then releases.
 */
export function useResolutionHold(t0) {
  const $ = _c(17);
  const {
    marketPubkey,
    market,
    winnerRowLoaded,
    winnersQueryKey
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== market) {
    t1 = () => market === undefined ? null : initialResolutionHold(market.status);
    $[0] = market;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [hold, setHold] = useState(t1);
  const [timedOut, setTimedOut] = useState(false);
  let current;
  if ($[2] !== hold || $[3] !== market || $[4] !== timedOut || $[5] !== winnerRowLoaded) {
    current = hold;
    if (market !== undefined) {
      const start = current ?? initialResolutionHold(market.status);
      current = nextResolutionHold(start, market, isSettledPageReady(market, winnerRowLoaded), timedOut);
    }
    $[2] = hold;
    $[3] = market;
    $[4] = timedOut;
    $[5] = winnerRowLoaded;
    $[6] = current;
  } else {
    current = $[6];
  }
  if (current !== hold) {
    setHold(current);
    if (timedOut) {
      setTimedOut(false);
    }
  }
  const winnersQueryKeyRef = useRef(winnersQueryKey);
  let t2;
  if ($[7] !== winnersQueryKey) {
    t2 = () => {
      winnersQueryKeyRef.current = winnersQueryKey;
    };
    $[7] = winnersQueryKey;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  useEffect(t2);
  let t3;
  if ($[9] !== current || $[10] !== marketPubkey || $[11] !== queryClient) {
    t3 = () => {
      if (current !== "holding") {
        return;
      }
      const marketHash = hashDiagnosticEntity(marketPubkey);
      const marketKey = ["markets", "entity", marketPubkey];
      const winnersKey = winnersQueryKeyRef.current;
      const refetch = () => {
        Promise.all([refetchRealtimeQuery(queryClient, marketKey), refetchRealtimeQuery(queryClient, winnersQueryKeyRef.current)]);
      };
      const first = setTimeout(refetch, RESOLUTION_HOLD_FIRST_REFETCH_MS);
      const interval = setInterval(refetch, RESOLUTION_HOLD_REFETCH_INTERVAL_MS);
      const timeout = setTimeout(() => {
        logger.warn("market.resolution_hold_timed_out", {
          marketHash
        });
        setTimedOut(true);
      }, RESOLUTION_HOLD_TIMEOUT_MS);
      return () => {
        clearTimeout(first);
        clearInterval(interval);
        clearTimeout(timeout);
        queryClient.cancelQueries({
          queryKey: marketKey,
          exact: true
        });
        queryClient.cancelQueries({
          queryKey: winnersKey,
          exact: true
        });
      };
    };
    $[9] = current;
    $[10] = marketPubkey;
    $[11] = queryClient;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== current || $[14] !== marketPubkey || $[15] !== queryClient) {
    t4 = [current, marketPubkey, queryClient];
    $[13] = current;
    $[14] = marketPubkey;
    $[15] = queryClient;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  useEffect(t3, t4);
  return current ?? "watching";
}